import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  downloadCompanyMonthlyBillingCsvById,
  downloadCompanyStatsCsvById,
  loadCompanyById,
  loadCompanyStats,
  loadSelectedCompanyBilling,
} from '../../../state/admin.actions';
import {
  selectCompany,
  selectCompanyBilling,
  selectCompanyStats,
  selectDownloadingCompanyMonthlyBillingCsvById,
  selectDownloadingCompanyStatsByIdCsv,
} from '../../../state/admin.selector';
import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ChartCardComponent } from '../../components/chart-card/chart-card.component';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import moment from 'moment/moment';
import { map, Observable, of } from 'rxjs';
import {
  ChartData,
  getChartSeriesFromFiles,
} from '../../../../../common/helpers/chart-utils';
import { CompanyStats } from '../../../domain/models/company/company-stats';
import { ChartFileInfo } from '../../../domain/models/file/chart-file-info';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatepickerUtils } from '../../../../../common/helpers/date-picker-utils';
import { formatCnpj } from '../../../../../common/helpers/company-utils';
import { CurrencyUtils } from '../../../../../common/helpers/currency-utils';

@Component({
  selector: 'app-company-details',
  standalone: true,
  imports: [
    AsyncPipe,
    ChartCardComponent,
    NgxDaterangepickerBootstrapDirective,
    RouterLink,
    TranslatePipe,
    CurrencyPipe,
    DatePipe,
  ],
  templateUrl: './company-details.component.html',
  styleUrl: './company-details.component.scss',
})
export class CompanyDetailsComponent implements OnInit {
  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false })
  pickerDirective!: NgxDaterangepickerBootstrapDirective;
  chartFiles$!: Observable<ChartFileInfo[]>;
  chartData$!: Observable<ChartData>;
  defaultDateRange = {
    startDate: moment().subtract(1, 'year').startOf('day'),
    endDate: moment().endOf('day'),
  };
  selectedDates:
    | { startDate: moment.Moment; endDate: moment.Moment }
    | undefined;
  protected readonly currencyUtils = inject(CurrencyUtils);
  protected datePickerUtils = inject(DatepickerUtils);
  protected translate = inject(TranslateService);
  protected datepickerLocale = {};
  protected readonly formatCnpj = formatCnpj;
  protected currentLang: string | undefined;
  protected currencyCode: string | undefined;
  private route = inject(ActivatedRoute);
  protected companyId = this.route.snapshot.paramMap.get('id');
  private store = inject(Store);
  protected company$ = this.store.select(selectCompany);
  protected companyStats$ = this.store.select(selectCompanyStats);
  protected companyBilling$ = this.store.select(selectCompanyBilling);
  protected isDownloadingStatsCsv$ = this.companyId
    ? this.store.select(selectDownloadingCompanyStatsByIdCsv(this.companyId))
    : of(false);
  protected isDownloadingBillingCsv$ = this.companyId
    ? this.store.select(
        selectDownloadingCompanyMonthlyBillingCsvById(this.companyId),
      )
    : of(false);

  get isFilterApplied(): boolean {
    return (
      this.selectedDates !== undefined &&
      (!this.selectedDates.startDate.isSame(
        this.defaultDateRange.startDate,
        'day',
      ) ||
        !this.selectedDates.endDate.isSame(
          this.defaultDateRange.endDate,
          'day',
        ))
    );
  }

  downloadCompanyBillingCsv() {
    if (!this.companyId) return;

    this.company$.pipe(map((c) => c?.name)).subscribe((companyName) => {
      if (!companyName) return;

      this.store.dispatch(
        downloadCompanyMonthlyBillingCsvById({
          companyId: this.companyId!,
          companyName,
        }),
      );
    });
  }

  onDateRangeChange(selected: any) {
    if (!selected?.startDate || !selected?.endDate) return;

    const startDate = selected.startDate.startOf('day').toDate();
    const endDate = selected.endDate.endOf('day').toDate();

    this.selectedDates = {
      startDate: moment(startDate),
      endDate: moment(endDate),
    };
    if (this.companyId) {
      this.store.dispatch(
        loadCompanyStats({
          id: this.companyId,
          startDate: startDate,
          endDate: endDate,
        }),
      );
    }

    this.chartFiles$ = this.companyStats$.pipe(
      map((stats: CompanyStats | null) => stats?.files ?? []),
    );

    this.chartData$ = getChartSeriesFromFiles(
      this.chartFiles$,
      startDate,
      endDate,
    );
  }

  updateLocale() {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;
    });
  }

  downloadStatsCsv() {
    if (!this.companyId) return;

    this.company$.pipe(map((c) => c?.name)).subscribe((companyName) => {
      if (!companyName) return;

      const startDate = this.selectedDates?.startDate?.startOf('day')?.toDate();
      const endDate = this.selectedDates?.endDate?.endOf('day')?.toDate();

      if (!startDate || !endDate) return;

      this.store.dispatch(
        downloadCompanyStatsCsvById({
          companyId: this.companyId!,
          companyName,
          startDate,
          endDate,
        }),
      );
    });
  }

  clearFilters(): void {
    this.selectedDates = undefined;
    if (this.companyId) {
      this.store.dispatch(
        loadCompanyStats({
          id: this.companyId,
          startDate: this.defaultDateRange.startDate.toDate(),
          endDate: this.defaultDateRange.endDate.toDate(),
        }),
      );
    }

    if (this.pickerDirective) {
      this.pickerDirective.clear();
    }

    this.chartFiles$ = this.companyStats$.pipe(
      map((stats: CompanyStats | null) => stats?.files ?? []),
    );

    this.chartData$ = getChartSeriesFromFiles(
      this.chartFiles$,
      this.defaultDateRange.startDate.toDate(),
      this.defaultDateRange.endDate.toDate(),
    );
  }

  ngOnInit(): void {
    this.updateLocale();

    this.translate.onLangChange.subscribe(() => {
      this.updateLocale();
    });

    if (this.companyId) {
      this.store.dispatch(loadCompanyById({ id: this.companyId }));
      this.store.dispatch(
        loadCompanyStats({
          id: this.companyId,
          startDate: this.defaultDateRange.startDate.toDate(),
          endDate: this.defaultDateRange.endDate.toDate(),
        }),
      );

      this.chartFiles$ = this.companyStats$.pipe(
        map((stats: CompanyStats | null) => stats?.files ?? []),
      );

      this.chartData$ = getChartSeriesFromFiles(
        this.chartFiles$,
        this.defaultDateRange.startDate.toDate(),
        this.defaultDateRange.endDate.toDate(),
      );

      this.store.dispatch(loadSelectedCompanyBilling({ id: this.companyId }));
    }

    this.currentLang = this.translate.currentLang || 'pt';
    this.currencyCode = this.currencyUtils.getCurrencyByLang(this.currentLang);

    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
      this.currencyCode = this.currencyUtils.getCurrencyByLang(event.lang);
    });
  }
}
